import {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAuthContext} from "../../../contexts/AuthContext";
import {usePermissions} from "../../../hooks/usePermissions";

// Р4 — автоматичен изход след 40 минути без действие. Админът е освободен.
//
// Броенето е по СТЕНЕН ЧАСОВНИК (запомнен момент на последното действие), а не
// по setTimeout: ако лаптопът се приспи за час, таймерът не тече, а човекът все
// пак е бил далеч от машината цял час. Меродавен е изтеклото време, не тиковете.
//
// Моментът се пази в localStorage, затова всички отворени раздели броят заедно —
// работа в един раздел не оставя другия да „узрее" за изход.
//
// Две минути преди изхода излиза предупреждение. Докато то стои, обикновеното
// движение на мишката НЕ отлага изхода: иначе човек би го изгасил, без да го е
// прочел, само защото е посегнал към мишката. Иска се натискане на „Оставам".
//
// ВАЖНО за очакванията: това е защита срещу изоставено работно място, не срещу
// откраднат токен. Самият токен на сървъра остава валиден 12 часа — тази мярка
// е в браузъра.

const МИНУТА = 60 * 1000;
const ИЗХОД_СЛЕД = 40 * МИНУТА;
const ПРЕДУПРЕЖДЕНИЕ_ПРЕДИ = 2 * МИНУТА;
const ЗАПИС_НЕ_ПО_ЧЕСТО_ОТ = 20 * 1000;
const КЛЮЧ = "lastActivity";

const СЪБИТИЯ = ["mousedown", "mousemove", "keydown", "wheel", "touchstart", "scroll"];

const прочетиПоследно = () => {
    const v = Number(localStorage.getItem(КЛЮЧ));
    return Number.isFinite(v) && v > 0 ? v : 0;
};

const катоЧасовник = (секунди) =>
    `${Math.floor(секунди / 60)}:${String(секунди % 60).padStart(2, "0")}`;

export const IdleLogout = () => {
    const navigate = useNavigate();
    const {isAuthenticated, setLocalStorageState} = useAuthContext();
    const {isAdmin} = usePermissions();

    const [оставатСекунди, setОставатСекунди] = useState(null);
    const предупреждава = useRef(false);
    const последенЗапис = useRef(0);

    // Записва се най-много веднъж на 20 секунди — при праг от 40 минути тази
    // точност стига, а иначе всяко помръдване на мишката би пишело в диска.
    const отбележиДейност = useCallback(() => {
        if (предупреждава.current) return;
        const сега = Date.now();
        if (сега - последенЗапис.current < ЗАПИС_НЕ_ПО_ЧЕСТО_ОТ) return;
        последенЗапис.current = сега;
        localStorage.setItem(КЛЮЧ, String(сега));
    }, []);

    const нулирай = useCallback(() => {
        предупреждава.current = false;
        setОставатСекунди(null);
        последенЗапис.current = Date.now();
        localStorage.setItem(КЛЮЧ, String(последенЗапис.current));
    }, []);

    const излез = useCallback(() => {
        предупреждава.current = false;
        setОставатСекунди(null);
        localStorage.removeItem(КЛЮЧ);
        setLocalStorageState({});
        navigate("/login", {state: {reason: "idle"}});
    }, [navigate, setLocalStorageState]);

    useEffect(() => {
        if (!isAuthenticated || isAdmin) return;

        // Часовникът тръгва отначало при всяко вписване и при презареждане на
        // страницата. Без това стар момент от предишна сесия би изхвърлил
        // човека веднага след като се е вписал.
        последенЗапис.current = Date.now();
        localStorage.setItem(КЛЮЧ, String(последенЗапис.current));

        СЪБИТИЯ.forEach(e =>
            document.addEventListener(e, отбележиДейност, {passive: true, capture: true}));

        const тик = setInterval(() => {
            const последно = прочетиПоследно() || Date.now();
            const остават = ИЗХОД_СЛЕД - (Date.now() - последно);

            if (остават <= 0) {
                излез();
            } else if (остават <= ПРЕДУПРЕЖДЕНИЕ_ПРЕДИ) {
                предупреждава.current = true;
                setОставатСекунди(Math.ceil(остават / 1000));
            } else if (предупреждава.current) {
                // Друг раздел е отчел действие — предупреждението пада.
                предупреждава.current = false;
                setОставатСекунди(null);
            }
        }, 1000);

        return () => {
            clearInterval(тик);
            СЪБИТИЯ.forEach(e =>
                document.removeEventListener(e, отбележиДейност, {capture: true}));
        };
    }, [isAuthenticated, isAdmin, отбележиДейност, излез]);

    if (оставатСекунди === null) return null;

    return (
        <>
            <div className="modal-backdrop show" style={{zIndex: 2070}}/>
            <div className="modal d-block" style={{zIndex: 2080}} role="dialog" aria-modal="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content"
                         style={{border: "1px solid var(--app-danger-fg)"}}>
                        <div className="modal-header">
                            <h5 className="modal-title" style={{color: "var(--app-danger-fg)"}}>
                                Още сте ли тук?
                            </h5>
                        </div>
                        <div className="modal-body">
                            <p className="mb-2">
                                Няма действие от 40 минути. Ще излезете автоматично след{" "}
                                <strong>{катоЧасовник(оставатСекунди)}</strong>.
                            </p>
                            <p className="mb-0 text-muted" style={{fontSize: "0.9rem"}}>
                                Незаписаното в отворена форма ще се загуби — натиснете
                                „Оставам" и запишете, преди да се отдалечите.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary"
                                    onClick={излез}>
                                Изход сега
                            </button>
                            <button type="button" className="btn btn-primary"
                                    autoFocus onClick={нулирай}>
                                Оставам
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
